import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { POISearchResult } from '@alltrails/types/AlgoliaResults';
import useDebouncedValue from '@alltrails/core/hooks/useDebouncedValue';
import { useGetPOITrailsQuery } from '../redux/exploreServiceApi';
import {
  ExploreServiceState,
  updateSearchIsLoading,
  updateSearchResults,
  updateSearchResultsPlace,
  updateSearchResultsSummary
} from '../redux/exploreServiceSlice';
import { PlaceLocation } from '../types/ExploreServiceLocation';
import useLocationExploreServiceQueryArgs from './useLocationExploreServiceQueryArgs';

export function usePOITrailResults(poi?: POISearchResult) {
  const dispatch = useDispatch();
  const skipQuery = useSelector((state: { exploreService: ExploreServiceState }) => state.exploreService.skipQuery);

  const memoizedQueryArgs = useLocationExploreServiceQueryArgs(poi);
  const debouncedQueryArgs = useDebouncedValue(memoizedQueryArgs, 100);

  const { data, isFetching, isLoading, isUninitialized } = useGetPOITrailsQuery(debouncedQueryArgs, {
    skip:
      skipQuery ||
      !poi ||
      !(debouncedQueryArgs.location as PlaceLocation)?.placeType ||
      ('bottomLeft' in debouncedQueryArgs.location &&
        (debouncedQueryArgs.location.bottomLeft === undefined ||
          debouncedQueryArgs.location.bottomRight === undefined ||
          debouncedQueryArgs.location.topLeft === undefined ||
          debouncedQueryArgs.location.topRight === undefined ||
          debouncedQueryArgs.location.mapRotation === undefined))
  });

  const searchIsLoading = useMemo(() => isFetching || isLoading || isUninitialized, [isFetching, isLoading, isUninitialized]);

  useEffect(() => {
    dispatch(updateSearchIsLoading(searchIsLoading));
  }, [dispatch, searchIsLoading]);

  useEffect(() => {
    dispatch(updateSearchResults(data?.searchResults ?? []));
  }, [dispatch, data?.searchResults]);

  useEffect(() => {
    dispatch(updateSearchResultsSummary(data?.summary));
  }, [dispatch, data?.summary]);

  useEffect(() => {
    dispatch(updateSearchResultsPlace(data?.place));
  }, [dispatch, data?.place]);
}
